import { Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { merge } from 'rxjs';
import { Country } from '../model/default-config.model';
import { AppState } from '../store';
import * as fromConfig from '../store/config/config.reducer';
import * as fromProcess from '../store/process/process.reducer';
import { FormsService } from './forms.service';
import { PersistStorageKeys, PersistStorageService } from './persist-storage.service';
import { ProcessService } from './process.service';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private processKind$ = this.store.select(fromProcess.selectProcessKind);
  private selectedPackageName$ = this.store.select(fromProcess.selectSelectedPackageName);
  private insureds$ = this.store.select(fromProcess.selectQuotationInsureds);
  private discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  private countriesConfig: Signal<Country[] | undefined> = this.store.selectSignal(fromConfig.selectConfigTripAbroadCountries);

  constructor(
    private formsService: FormsService,
    private store: Store<AppState>,
    private persistStorageService: PersistStorageService,
    private processService: ProcessService,
  ) {
    this.persistStorageService.setString(PersistStorageKeys.SessionID, this.processService.apiPayload.interactionData.sessionId, 'session');
    this.persistStorageService.setString(PersistStorageKeys.Partner, this.getPartner(), 'session');

    this.discountCode$.pipe(takeUntilDestroyed()).subscribe((discountCode) => {
      if (discountCode) {
        this.persistStorageService.setString(PersistStorageKeys.DiscountCode, discountCode, 'session');
      }
    });

    this.processKind$.pipe(takeUntilDestroyed()).subscribe((processKind) => {
      if (processKind) {
        this.persistStorageService.setString(PersistStorageKeys.Path, processKind, 'session');
      }
    });

    this.selectedPackageName$.pipe(takeUntilDestroyed()).subscribe((selectedPackageName) => {
      if (selectedPackageName) {
        this.persistStorageService.setString(PersistStorageKeys.PackageName, selectedPackageName, 'session');
      }
    });

    this.formsService.tripAbroad.controls.destination.valueChanges.pipe(takeUntilDestroyed()).subscribe((destinationId) => {
      if (destinationId) {
        const destination = this.countriesConfig()?.find(
          (country) => country.countryId.toString() === destinationId.replace(/[a-z]/gi, ''),
        );
        const destinationName = destination?.countryName;
        const region = destination?.zone;

        if (destinationName) {
          this.persistStorageService.setString(PersistStorageKeys.Country, destinationName, 'session');
        }
        if (region) {
          this.persistStorageService.setString(PersistStorageKeys.Region, region, 'session');
        }
      }
    });

    merge(
      this.formsService.tripAbroad.controls.travelDates.controls.dateFrom.valueChanges,
      this.formsService.travelInPl.controls.travelDates.controls.dateFrom.valueChanges,
      this.formsService.resignation.controls.travelDates.controls.dateFrom.valueChanges,
    )
      .pipe(takeUntilDestroyed())
      .subscribe((dateFrom) => {
        if (dateFrom) {
          this.persistStorageService.setString(PersistStorageKeys.DateFrom, dateFrom, 'session');
        }
      });

    merge(
      this.formsService.tripAbroad.controls.travelDates.controls.dateTo.valueChanges,
      this.formsService.travelInPl.controls.travelDates.controls.dateTo.valueChanges,
      this.formsService.resignation.controls.travelDates.controls.dateTo.valueChanges,
    )
      .pipe(takeUntilDestroyed())
      .subscribe((dateTo) => {
        if (dateTo) {
          this.persistStorageService.setString(PersistStorageKeys.DateTo, dateTo, 'session');
        }
      });

    merge(
      this.formsService.tripAbroad.controls.numberOfInsureds.valueChanges,
      this.formsService.travelInPl.controls.numberOfInsureds.valueChanges,
      this.formsService.resignation.controls.numberOfInsureds.valueChanges,
    )
      .pipe(takeUntilDestroyed())
      .subscribe((numberOfInsureds) => {
        if (numberOfInsureds) {
          this.persistStorageService.setString(PersistStorageKeys.NumberOfInsureds, numberOfInsureds.toString(), 'session');
        }
      });

    this.insureds$.pipe(takeUntilDestroyed()).subscribe((insureds) => {
      if (insureds) {
        if (insureds) {
          this.persistStorageService.setString(
            PersistStorageKeys.InsuredBirthYear,
            insureds.map((insured) => insured.birthYear).toString(),
            'session',
          );
        }
      }
    });
  }

  private getPartner(): string {
    const url = new URL(window.location.href);

    return (url.searchParams.get('partner') as string) ?? '';
  }
}
